import { v4 as uuid } from 'uuid'

/**
 * Bouwt het Firestore-document voor een projectafspraak: een taak die de
 * beheertenant bij een klantproject vastlegt. Het document leeft in de EIGEN
 * omgeving (`CompagnyID`) en wijst met `ProjectID` naar het project.
 *
 * Pure functie met validatie. Hij gooit niets — hij geeft de fouten terug,
 * zodat het venster ze naast de invulvelden kan tonen.
 * AI-CONTEXT: See docs/features/deccos-projectafspraken.md
 *
 * @typedef {object} ProjectTaskInput
 * @property {string} projectId - CompagnyID van het klantproject
 * @property {string} [projectName] - Naam van het project, meegeschreven zodat de agenda hem kan tonen
 * @property {string} compagnyId - Tenant die de afspraak vastlegt
 * @property {string} userId - Auteur
 * @property {string} title
 * @property {string} [description]
 * @property {string} [date] - 'YYYY-MM-DD' uit het datumveld. Leeg mag: een afspraak
 *   zonder datum staat wel in de lijst, maar nog niet in de agenda
 * @property {string} [startTime] - 'HH:MM'; leeg = middernacht
 * @property {string} [endTime] - 'HH:MM'; leeg = geen eindtijd
 * @property {boolean} [allDay] - Hele dag: geen tijden, geen eindtijd
 * @property {Array<string>} [teamMembers]
 * @property {number} [position]
 * @property {(date: Date) => any} toTimestamp - Zet een Date om in een Firestore-Timestamp
 *
 * @param {ProjectTaskInput} input
 * @returns {{errors: Array<string>, task: object|null}}
 */
const buildProjectTask = ({
    projectId,
    projectName = '',
    compagnyId,
    userId,
    title,
    description = '',
    date,
    startTime = '',
    endTime = '',
    allDay = false,
    teamMembers = [],
    position = 0,
    toTimestamp,
}) => {
    const errors = []
    const trimmedTitle = (title || '').trim()

    if (!trimmedTitle) errors.push('Geef de afspraak een titel.')
    if (!projectId) errors.push('Er hoort een project bij deze afspraak.')

    // Zonder datum blijft de afspraak staan als losse actiepunt: wel in de
    // lijst, nog niet in de agenda. Tijden zonder datum zeggen niets en gaan
    // dus mee de prullenbak in.
    const start = date ? toDate(date, allDay ? '' : startTime) : null
    const end = start && !allDay && endTime ? toDate(date, endTime) : null

    if (start && end && end <= start) {
        errors.push('De eindtijd ligt voor de starttijd.')
    }

    if (errors.length > 0) {
        return { errors, task: null }
    }

    return {
        errors: [],
        task: {
            ID: uuid(),
            Type: 'projectFollowUp',
            ProjectID: projectId,
            ProjectName: projectName || '',
            CompagnyID: compagnyId,
            Compagny: compagnyId,
            Title: trimmedTitle,
            Description: description,
            Tags: [],
            TeamMembers: teamMembers,
            Position: position,
            Done: false,
            User: userId,
            Timestamp: start ? toTimestamp(start) : null,
            TimestampEnd: end ? toTimestamp(end) : null,
            Parent: null,
            rrule: null,
            exdate: [],
            exrule: null,
        },
    }
}

/**
 * Zet een datumveld ('YYYY-MM-DD') plus tijdveld ('HH:MM') om in een lokale
 * Date. Lege tijd = middernacht.
 *
 * @param {string} date
 * @param {string} time
 * @returns {Date|null}
 */
const toDate = (date, time) => {
    if (!date) return null

    const [year, month, day] = date.split('-').map(Number)
    if (!year || !month || !day) return null

    const [hours, minutes] = (time || '00:00').split(':').map(Number)

    return new Date(year, month - 1, day, hours || 0, minutes || 0)
}

export default buildProjectTask
